import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import UseContext from "hooks/useContext";
import { Check, ChevronDown } from "assets/icons";
import {
    getPerimeterFilterChoices,
    NO_PERIMETER_FILTER,
} from "components/perimeterFilter/perimeterListFilter";
import { fillText } from "pages/profil/perimeters/perimeterText";

const MENU_WIDTH = 288;
const VIEWPORT_MARGIN = 8;

const PerimeterFilterChoice = ({ label, hint, isChecked, onToggle }) => (
    <li
        role="option"
        aria-selected={isChecked}
        onClick={onToggle}
        className={`flex cursor-pointer items-start gap-3 px-4 py-2.5 ${
            isChecked ? "bg-yellow-50" : "hover:bg-gray-50"
        }`}
    >
        <span
            aria-hidden="true"
            className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                isChecked
                    ? "border-yellow-500 bg-yellow-500"
                    : "border-gray-300 bg-white"
            }`}
        >
            {isChecked && <Check wh={12} color="#ffffff" />}
        </span>
        <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm text-gray-900">{label}</span>
            {hint && <span className="text-xs text-gray-500">{hint}</span>}
        </span>
    </li>
);

// "Perimeter" button of a list toolbar: a multiple choice menu. The menu goes
// through a portal because the sub navbar scrolls horizontally and would clip it.
const PerimeterFilterButton = ({ options, selectedIds, onChange }) => {
    const { language } = UseContext();
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const choices = getPerimeterFilterChoices(options);

    const noPerimeterLabel = language.getValue(
        "perimeters.listFilter.noPerimeter"
    );
    const getChoiceLabel = (choice) =>
        choice.value === NO_PERIMETER_FILTER ? noPerimeterLabel : choice.name;

    const updateMenuPosition = useCallback(() => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;
        setMenuPosition({
            top: rect.bottom + 4,
            left: Math.max(
                VIEWPORT_MARGIN,
                Math.min(
                    rect.left,
                    window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN
                )
            ),
        });
    }, []);

    useEffect(() => {
        if (!isOpen) return undefined;
        updateMenuPosition();
        const handleMouseDown = (event) => {
            if (
                buttonRef.current?.contains(event.target) ||
                menuRef.current?.contains(event.target)
            ) {
                return;
            }
            setIsOpen(false);
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") setIsOpen(false);
        };
        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("scroll", updateMenuPosition, true);
        window.addEventListener("resize", updateMenuPosition);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("scroll", updateMenuPosition, true);
            window.removeEventListener("resize", updateMenuPosition);
        };
    }, [isOpen, updateMenuPosition]);

    // Keeps the menu order so the same choice always sends the same request.
    const toggleChoice = (value) => {
        const nextSelected = selectedIds.includes(value)
            ? selectedIds.filter((id) => id !== value)
            : [...selectedIds, value];
        onChange(
            choices
                .map((choice) => choice.value)
                .filter((choiceValue) => nextSelected.includes(choiceValue))
        );
    };

    const getButtonLabel = () => {
        if (selectedIds.length === 0) {
            return language.getValue("perimeters.listFilter.button");
        }
        if (selectedIds.length === 1) {
            const choice = choices.find(
                (item) => item.value === selectedIds[0]
            );
            return choice
                ? getChoiceLabel(choice)
                : language.getValue("perimeters.listFilter.button");
        }
        return fillText(language.getValue("perimeters.listFilter.count"), {
            count: selectedIds.length,
        });
    };

    const isActive = selectedIds.length > 0;

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((open) => !open)}
                className={`flex w-full tab:w-auto min-w-[160px] max-w-[260px] items-center justify-between gap-2 rounded-md border px-4 py-2 text-sm transition-colors ${
                    isActive
                        ? "border-yellow-500 bg-yellow-50 text-gray-900 hover:bg-yellow-100"
                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                }`}
            >
                <span className="truncate">{getButtonLabel()}</span>
                <span
                    aria-hidden="true"
                    className={`shrink-0 transition-transform ${
                        isOpen ? "rotate-180" : ""
                    }`}
                >
                    <ChevronDown wh={16} color="#374151" />
                </span>
            </button>
            {isOpen &&
                createPortal(
                    <div
                        ref={menuRef}
                        className="fixed z-[9999] rounded-md border border-gray-200 bg-white shadow-lg"
                        style={{
                            top: `${menuPosition.top}px`,
                            left: `${menuPosition.left}px`,
                            width: `${MENU_WIDTH}px`,
                        }}
                    >
                        <ul
                            role="listbox"
                            aria-multiselectable="true"
                            className="max-h-72 overflow-y-auto py-1"
                        >
                            {choices.map((choice) => (
                                <PerimeterFilterChoice
                                    key={choice.value}
                                    label={getChoiceLabel(choice)}
                                    hint={
                                        choice.value === NO_PERIMETER_FILTER
                                            ? language.getValue(
                                                  "perimeters.listFilter.noPerimeterHint"
                                              )
                                            : null
                                    }
                                    isChecked={selectedIds.includes(
                                        choice.value
                                    )}
                                    onToggle={() => toggleChoice(choice.value)}
                                />
                            ))}
                        </ul>
                        <div className="flex justify-end border-t border-gray-100 px-4 py-2">
                            <button
                                type="button"
                                disabled={!isActive}
                                onClick={() => onChange([])}
                                className="text-sm font-medium text-yellow-600 hover:underline disabled:cursor-default disabled:text-gray-400 disabled:no-underline"
                            >
                                {language.getValue("perimeters.listFilter.clear")}
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default PerimeterFilterButton;
